import { AxleButton } from '@/components/buttons/AxleButton';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import { useMaxServicesPerAppointment } from '@/hooks/organization/useMaxServicesPerAppointment';
import { FeatureFlag } from '@/types/featureFlag';
import { VisitOutcome } from '@/types/visit';
import { useFeatureFlagEnabledQuery } from '@/utils/useFeatureFlagEnabled';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { FC, useCallback } from 'react';

import {
    EditVisitServicesActionQuery,
    EditVisitServicesActionQueryVariables,
} from './EditVisitServicesButton.gql.types';

// Selects only fields a list query already writes to the cached `Visit`, so the cache
// redirect on `Query.visit` resolves it without a network round trip. Non-suspending on
// purpose: this hook is consumed inside table action cells that are not wrapped in a
// Suspense boundary.
const EDIT_VISIT_SERVICES_ACTION_QUERY = gql`
    query EditVisitServicesAction($visitId: UUID!) {
        visit(id: $visitId) {
            id
            outcome
            permissions {
                id
                editServices
            }
            clinicianAppointments {
                id
            }
        }
    }
`;

type EditVisitServicesAction = {
    canEditServices: boolean;
    label: string;
    open: () => void;
};

/**
 * Encapsulates the label + gating logic for the "Update Service(s)" action so surfaces only need to
 * pass a `visitId`. The action is available only for single-clinician-appointment visits with no
 * outcome, when the user has `editServices` permission and the feature flag is enabled.
 */
export const useEditVisitServicesAction = (visitId: string): EditVisitServicesAction => {
    const { openDialog } = useDialog();
    const { data } = useQuery<EditVisitServicesActionQuery, EditVisitServicesActionQueryVariables>(
        EDIT_VISIT_SERVICES_ACTION_QUERY,
        { variables: { visitId } },
    );

    const visit = data?.visit ?? null;
    // The surfaces that render this action name the flag in their own flags query, so this
    // resolves from the cache rather than costing a request per row.
    const { enabled: flagEnabled } = useFeatureFlagEnabledQuery(
        FeatureFlag.DASHBOARD_EDIT_VISIT_SERVICES_DIALOG,
    );
    const { data: maxServicesPerAppointment } = useMaxServicesPerAppointment();
    const appointments = visit?.clinicianAppointments ?? [];
    const clinicianAppointmentId = appointments.length === 1 ? appointments[0].id : null;

    const canEditServices =
        flagEnabled &&
        visit !== null &&
        visit.permissions.editServices &&
        visit.outcome === VisitOutcome.NONE &&
        clinicianAppointmentId !== null;

    const label = maxServicesPerAppointment === 1 ? 'Update Service' : 'Update Services';

    const open = useCallback(() => {
        if (clinicianAppointmentId) {
            openDialog(DialogType.EditVisitServices, { clinicianAppointmentId });
        }
    }, [openDialog, clinicianAppointmentId]);

    return { canEditServices, label, open };
};

export const EditVisitServicesButton: FC<{ visitId: string }> = ({ visitId }) => {
    const { canEditServices, label, open } = useEditVisitServicesAction(visitId);

    if (!canEditServices) {
        return null;
    }

    return <AxleButton onClick={open}>{label}</AxleButton>;
};
